import type { ComponentType } from "react"
import { useEffect, useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import {
	IconAtom2Fill18,
	IconBookOpen4Fill18,
	IconEnvelopeFill18,
	IconEyeSlashFill18,
	IconTextAlignLeftFill18,
} from "nucleo-ui-fill-18"
import {
	IconArrowRotateAnticlockwiseOutline18 as ArrowRotate,
	IconAtom2Outline18 as Atom,
	IconChevronDownOutline18 as ChevronDown,
	IconCircleCheckOutline18 as CircleCheck,
	IconCircleXmarkOutline18 as CircleXmark,
	IconDotsOutline18 as Dots,
	IconEyeSlashOutline18 as EyeSlash,
	IconPaperPlaneOutline18 as PaperPlane,
	IconPenOutline18 as Pen,
	IconXmarkOutline18 as Xmark,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import {
	AlertDialog,
	Button,
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
	IconButton,
	Loader,
	Popover,
	Textarea,
	Tooltip,
	typographyVariants,
} from "@productlane/ui"
import { MicrosoftTeamsIcon } from "@productlane/ui/src/icons/project/microsoftteams-icon"
import { SlackIcon } from "@productlane/ui/src/icons/project/slack-icon"

import type {
	ShadowReplyChannel,
	ShadowReplyLesson,
	ShadowReplyStatus,
} from "./shadow-reply"
import type { TimelineEventProps } from "./types"
import { EmailEditor } from "@/components/editor/email-editor"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { captureError } from "@/lib/capture-error"
import { sanitizeMessageHtml } from "@/lib/sanitize-message-html"
import { useRole } from "@/lib/useRole"
import { useThreadContext } from "../../../thread-context"
import { AgentMessageAvatar } from "./base/agent-message-avatar"
import { SlackStyleMessage } from "./base/slack-style-message"
import { AI_CHAT_ACTOR_NAME } from "./improve-reply"
import {
	parseShadowReplyPayload,
	SHADOW_REPLY_EVENT_TYPE,
	shadowReplyActions,
	shadowReplyChannelLabel,
	shadowReplyStatusLine,
} from "./shadow-reply"

const AGENT_SETTINGS_PATH = "/settings/ai/agent"
const DOCS_PATH = "/edit-docs"

const CHANNEL_TOAST_ICONS: Record<
	ShadowReplyChannel,
	ComponentType<{ className?: string }>
> = {
	email: IconEnvelopeFill18,
	slack: SlackIcon,
	teams: MicrosoftTeamsIcon,
	support_portal: IconAtom2Fill18,
	api: IconAtom2Fill18,
}

// Where the switch to turn the channel live sits. Kept apart from the review actions,
// because it changes a setting rather than deciding on this one reply.
export type ActivatePlacement = "row-end" | "header" | "below"

export function ShadowReplyEvent({
	event,
	activatePlacement = "row-end",
}: TimelineEventProps & { activatePlacement?: ActivatePlacement }) {
	const { thread } = useThreadContext()
	const { role } = useRole()
	const navigate = useNavigate()
	const [localStatus, setLocalStatus] = useState<ShadowReplyStatus | null>(null)
	const [isActivateOpen, setIsActivateOpen] = useState(false)
	// Both regenerate and decline ask for a note first: it is what teaches the agent.
	const [noteMode, setNoteMode] = useState<"regenerate" | "decline" | null>(
		null,
	)
	const [note, setNote] = useState("")
	const [localNote, setLocalNote] = useState<string | null>(null)
	// What the note taught, shown before the synced payload carries it.
	const [localLesson, setLocalLesson] = useState<ShadowReplyLesson | null>(null)
	const [isDetailOpen, setIsDetailOpen] = useState(false)
	const [isLessonUndone, setIsLessonUndone] = useState(false)
	// The teammate's version of the reply while they edit it; null when not editing.
	const [editedHtml, setEditedHtml] = useState<string | null>(null)
	const [isEditEmpty, setIsEditEmpty] = useState(false)

	const sendMutation = api.shadowReplies.send.useMutation()
	const regenerateMutation = api.shadowReplies.regenerate.useMutation()
	const declineMutation = api.shadowReplies.decline.useMutation()
	const noteMutation =
		noteMode === "decline" ? declineMutation : regenerateMutation

	// The synced status wins once it moves, so a rewrite that finished or failed never sticks.
	const syncedStatus =
		event.type === "activity"
			? (event.threadActivity.payload as { status?: unknown } | null)?.status
			: undefined
	useEffect(() => {
		setLocalStatus(null)
	}, [syncedStatus])
	const activateMutation = api.shadowReplies.activateChannel.useMutation()
	const stopMutation = api.shadowReplies.stopPreviews.useMutation()
	const undoLessonMutation = api.shadowReplies.undoLesson.useMutation()

	if (event.type !== "activity") return null
	if (event.threadActivity.eventType !== SHADOW_REPLY_EVENT_TYPE) return null
	const payload = parseShadowReplyPayload(event.threadActivity.payload)
	if (!payload) return null

	const status = localStatus ?? payload.status
	if (status === "superseded" || payload.status === "superseded") return null

	const activityId = event.threadActivity.id
	const revisionNote = localNote ?? payload.revisionNote ?? null
	const channelLabel = shadowReplyChannelLabel(payload.channel)
	const actions = shadowReplyActions({
		status,
		role,
		isCloseLoop: !!payload.closeLoop,
	})
	const isBusy =
		sendMutation.isLoading ||
		regenerateMutation.isLoading ||
		declineMutation.isLoading ||
		activateMutation.isLoading

	const reportFailure = (error: unknown, action: string, title: string) => {
		captureError(error, {
			domain: "ai-chat",
			action,
			workspaceId: thread.workspaceId,
			extra: { activityId },
		})
		toast.error({
			title,
			description: error instanceof Error ? error.message : undefined,
			icon: IconAtom2Fill18,
		})
	}

	// An edited send also teaches the agent: the server diffs it against the draft.
	const handleSend = async () => {
		try {
			await sendMutation.mutateAsync(
				editedHtml === null ? { activityId } : { activityId, html: editedHtml },
			)
			setLocalStatus("sent")
			setEditedHtml(null)
		} catch (error) {
			reportFailure(error, "send shadow reply", "Couldn't send the reply")
		}
	}

	// The note becomes a rule or a help center change. Regenerate also has the agent write the preview again.
	const handleNote = async () => {
		const trimmed = note.trim()
		if (!noteMode || !trimmed || noteMutation.isLoading) return
		const mode = noteMode
		let result: Awaited<ReturnType<typeof regenerateMutation.mutateAsync>>
		try {
			result = await noteMutation.mutateAsync({ activityId, note: trimmed })
		} catch (error) {
			reportFailure(
				error,
				`${mode} shadow reply`,
				mode === "decline"
					? "Couldn't decline the preview"
					: "Couldn't regenerate the preview",
			)
			return
		}
		setLocalStatus(mode === "decline" ? "declined" : "revising")
		setLocalNote(trimmed)
		if (result.target === "instructions") {
			setLocalLesson({ target: "instructions", rule: result.rule })
		} else if (result.target === "docs") {
			setLocalLesson({ target: "docs" })
		}
		setNoteMode(null)
		setNote("")
		if (result.target === "instructions") {
			toast.success({
				title: "Added to the agent's instructions",
				description: result.rule,
				icon: IconTextAlignLeftFill18,
				action: {
					label: "See instructions",
					callback: () => void navigate({ href: AGENT_SETTINGS_PATH }),
				},
			})
		} else if (result.target === "docs") {
			toast.success({
				title: "Updating the help center",
				description:
					"The change goes live as soon as the agent has written it.",
				icon: IconBookOpen4Fill18,
				action: {
					label: "Open Docs",
					callback: () => void navigate({ href: DOCS_PATH }),
				},
			})
		}
	}

	const handleActivate = async () => {
		try {
			await activateMutation.mutateAsync({ channel: payload.channel })
		} catch (error) {
			reportFailure(
				error,
				"activate agent channel",
				`Couldn't turn on auto-reply for ${channelLabel}`,
			)
			return
		}
		toast.success({
			title: `Auto-reply is on for ${channelLabel}`,
			description: "The agent now answers new messages there.",
			icon: CHANNEL_TOAST_ICONS[payload.channel],
		})
	}

	const handleStop = async () => {
		try {
			await stopMutation.mutateAsync()
		} catch (error) {
			reportFailure(
				error,
				"stop shadow replies",
				"Couldn't turn off agent previews",
			)
			return
		}
		toast.info({
			title: "Agent previews are off",
			description: "Turn them back on in the Productlane Agent settings.",
			icon: IconEyeSlashFill18,
			action: {
				label: "Open settings",
				callback: () => void navigate({ href: AGENT_SETTINGS_PATH }),
			},
		})
	}

	const syncedLesson = payload.lesson ?? localLesson
	const lesson =
		syncedLesson && isLessonUndone
			? { ...syncedLesson, undone: true }
			: syncedLesson
	const statusLine = shadowReplyStatusLine({
		status,
		originalHtml: payload.originalHtml,
		note: revisionNote,
		lesson,
	})
	const canUndo =
		!!role &&
		role !== "VIEWER" &&
		lesson?.target === "instructions" &&
		!lesson.undone

	const handleUndoLesson = async () => {
		try {
			await undoLessonMutation.mutateAsync({ activityId })
		} catch (error) {
			reportFailure(
				error,
				"undo shadow reply lesson",
				"Couldn't remove the rule",
			)
			return
		}
		setIsLessonUndone(true)
		setIsDetailOpen(false)
		toast.success({
			title: "Removed from the agent's instructions",
			icon: IconTextAlignLeftFill18,
		})
	}

	const detail = statusLine?.trigger ? (
		<div className="flex flex-col gap-3">
			{statusLine.note && (
				<div className="flex flex-col gap-1">
					<span className="text-xs font-medium text-tertiary">Your note</span>
					<p className="whitespace-pre-wrap text-sm text-primary">
						{statusLine.note}
					</p>
				</div>
			)}
			{statusLine.lesson?.target === "instructions" && (
				<div
					className={cn(
						"flex flex-col gap-1",
						statusLine.note &&
							"border-t border-gray-200 pt-3 dark:border-gray-700/80",
					)}
				>
					<span className="text-xs font-medium text-tertiary">
						{statusLine.lesson.undone
							? "Removed from the agent's instructions"
							: "Added to the agent's instructions"}
					</span>
					<p
						className={cn(
							"text-sm text-primary",
							statusLine.lesson.undone && "text-tertiary line-through",
						)}
					>
						{statusLine.lesson.rule}
					</p>
				</div>
			)}
			{statusLine.lesson?.target === "docs" && (
				<div
					className={cn(
						"flex flex-col gap-1",
						statusLine.note &&
							"border-t border-gray-200 pt-3 dark:border-gray-700/80",
					)}
				>
					<span className="text-xs font-medium text-tertiary">Help center</span>
					<p className="text-sm text-primary">
						The agent suggested a change. It goes live once someone approves it.
					</p>
				</div>
			)}
			{statusLine.lesson && (
				<div className="flex items-center justify-end gap-2">
					{canUndo && (
						<Button
							size="sm"
							variant="ghost"
							isLoading={undoLessonMutation.isLoading}
							onClick={() => void handleUndoLesson()}
						>
							Undo
						</Button>
					)}
					<Button
						size="sm"
						variant="outline"
						onClick={() =>
							void navigate({
								href:
									statusLine.lesson?.target === "docs"
										? DOCS_PATH
										: AGENT_SETTINGS_PATH,
							})
						}
					>
						{statusLine.lesson.target === "docs"
							? "Open Docs"
							: "See instructions"}
					</Button>
				</div>
			)}
		</div>
	) : null

	const StatusIcon =
		status === "sent" ? CircleCheck : status === "declined" ? CircleXmark : null
	const decidedLine = statusLine ? (
		<>
			{StatusIcon ? (
				<StatusIcon className="h-3.5 w-3.5 flex-none" />
			) : (
				<Loader size={3} className="flex-none" />
			)}
			<span className="flex-none">{statusLine.label}</span>
			{statusLine.trigger && detail && (
				<>
					<span aria-hidden className="flex-none">
						·
					</span>
					<Popover
						open={isDetailOpen}
						onOpenChange={setIsDetailOpen}
						modal={false}
						align="start"
						className="w-80 p-3"
						content={detail}
					>
						<Button
							size="xs"
							variant="ghost"
							iconRight={ChevronDown}
							className="-ml-1 text-secondary"
						>
							{statusLine.trigger}
						</Button>
					</Popover>
				</>
			)}
		</>
	) : null

	const activateButton = actions.includes("activate") ? (
		<Button
			size="xs"
			variant="ghost"
			Icon={Atom}
			disabled={isBusy}
			onClick={() => setIsActivateOpen(true)}
		>
			Turn on auto-reply
		</Button>
	) : null

	const noteForm = (
		<div className="flex flex-col gap-2">
			<Textarea
				name="shadow-reply-note"
				autoFocus
				value={note}
				placeholder="What should the agent do differently? For example: keep it to two sentences."
				minRows={2}
				maxRows={6}
				disabled={noteMutation.isLoading}
				onChange={(event) => setNote(event.target.value)}
				onKeyDown={(event) => {
					if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
						event.preventDefault()
						void handleNote()
					}
					if (event.key === "Escape") setNoteMode(null)
				}}
			/>
			<div className="flex h-7 items-center gap-2">
				<Button
					size="sm"
					Icon={noteMode === "decline" ? Xmark : ArrowRotate}
					isLoading={noteMutation.isLoading}
					disabled={!note.trim() || noteMutation.isLoading}
					onClick={() => void handleNote()}
				>
					{noteMode === "decline" ? "Decline" : "Regenerate"}
				</Button>
				<Button
					size="sm"
					variant="ghost"
					disabled={noteMutation.isLoading}
					onClick={() => setNoteMode(null)}
				>
					Cancel
				</Button>
			</div>
		</div>
	)

	const editActions = (
		<div className="flex h-7 items-center gap-2">
			<Button
				size="sm"
				Icon={PaperPlane}
				isLoading={sendMutation.isLoading}
				disabled={isBusy || isEditEmpty}
				onClick={handleSend}
			>
				Send
			</Button>
			<Button
				size="sm"
				variant="ghost"
				disabled={sendMutation.isLoading}
				onClick={() => setEditedHtml(null)}
			>
				Cancel
			</Button>
		</div>
	)

	const isEditing = editedHtml !== null && actions.includes("edit")
	const showActivateInMenu =
		activatePlacement === "row-end" && actions.includes("activate")
	const moreMenu =
		!isEditing &&
		!noteMode &&
		(showActivateInMenu || actions.includes("stop")) ? (
			<DropdownMenu
				trigger={
					<IconButton
						Icon={Dots}
						variant="ghost"
						size="sm"
						aria-label="More actions"
					/>
				}
				contentProps={{ align: "end" }}
			>
				<DropdownMenuGroup>
					{showActivateInMenu && (
						<DropdownMenuItem
							Icon={Atom}
							disabled={isBusy}
							onClick={() => setIsActivateOpen(true)}
						>
							Turn on auto-reply
						</DropdownMenuItem>
					)}
					{actions.includes("stop") && (
						<DropdownMenuItem Icon={EyeSlash} onClick={handleStop}>
							Stop agent previews
						</DropdownMenuItem>
					)}
				</DropdownMenuGroup>
			</DropdownMenu>
		) : null

	const footer = isEditing ? (
		editActions
	) : actions.length > 0 && noteMode ? (
		noteForm
	) : actions.length > 0 ? (
		<div className="flex h-7 items-center gap-2">
			<Button
				size="sm"
				Icon={PaperPlane}
				isLoading={sendMutation.isLoading}
				disabled={isBusy}
				onClick={handleSend}
			>
				Send
			</Button>
			{actions.includes("edit") && (
				<Button
					size="sm"
					variant="outline"
					Icon={Pen}
					disabled={isBusy}
					onClick={() => {
						setIsEditEmpty(false)
						setEditedHtml(payload.html)
					}}
				>
					Edit
				</Button>
			)}
			{actions.includes("regenerate") && (
				<Button
					size="sm"
					variant="outline"
					Icon={ArrowRotate}
					disabled={isBusy}
					onClick={() => setNoteMode("regenerate")}
				>
					Regenerate
				</Button>
			)}
			<Button
				size="sm"
				variant="outline"
				Icon={Xmark}
				disabled={isBusy}
				onClick={() => setNoteMode("decline")}
			>
				Decline
			</Button>
		</div>
	) : decidedLine ? (
		<div className="flex h-7 items-center gap-1.5 text-xs text-tertiary">
			{decidedLine}
		</div>
	) : null

	return (
		<div className="py-1" data-shadow-reply={status}>
			{/* Padding is the bubble's minus the 1px border and the inner py-1, so text lines up with real bubbles. */}
			<div className="-mx-2 rounded-2xl border border-dashed border-blue-300 bg-blue-100 px-[15px] pb-[9px] pt-[11px] dark:border-blue-400/30 dark:bg-blue-500/15">
				<SlackStyleMessage
					showBackground={false}
					senderName={AI_CHAT_ACTOR_NAME}
					timestamp={new Date(event.threadActivity.createdAt)}
					isOutbound
					shrinkActions
					actions={
						<>
							<Tooltip
								label={`The customer sees this only after you send it on ${channelLabel}.`}
							>
								<span className="block min-w-0 truncate text-xs text-tertiary">
									Preview · Team only
								</span>
							</Tooltip>
							{activatePlacement === "header" && activateButton}
							{moreMenu}
						</>
					}
					avatarContent={<AgentMessageAvatar />}
					footer={footer}
				>
					{isEditing ? (
						<div className="rounded-lg bg-white px-2.5 py-2 ring-1 ring-blue-300 dark:bg-gray-900 dark:ring-blue-400/30">
							<EmailEditor
								initialContent={sanitizeMessageHtml(payload.html)}
								autofocus="end"
								className="cursor-text"
								onUpdate={({ editor }) => {
									setEditedHtml(editor.getHTML())
									setIsEditEmpty(editor.isEmpty)
								}}
							/>
						</div>
					) : (
						<div
							dangerouslySetInnerHTML={{
								__html: sanitizeMessageHtml(payload.html),
							}}
							className={typographyVariants({
								variant: "message",
								className:
									"min-w-full overflow-auto prose-a:text-green-600 hover:prose-a:underline dark:prose-a:text-green-500",
							})}
						/>
					)}
				</SlackStyleMessage>
			</div>
			{activatePlacement === "below" && activateButton && (
				<div className="flex items-center justify-end gap-1 pt-1 text-xs text-tertiary">
					<span>Auto-reply is off for {channelLabel}.</span>
					{activateButton}
				</div>
			)}
			{actions.includes("activate") && (
				<AlertDialog
					isOpen={isActivateOpen}
					onOpenChange={setIsActivateOpen}
					variant="positive"
					title={`Turn on auto-reply for ${channelLabel}?`}
					description={`The agent will answer new messages on ${channelLabel} by itself. You can change this anytime in the agent settings.`}
					confirmText="Activate auto-reply"
					onCancel={() => setIsActivateOpen(false)}
					onConfirm={handleActivate}
				/>
			)}
		</div>
	)
}
